import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faTimes } from '@fortawesome/pro-solid-svg-icons';
import {
	Dialog,
	DialogPanel,
	Transition,
	TransitionChild,
} from '@headlessui/react';
import React, {
	Fragment,
	useCallback,
	useEffect,
	useRef,
	useState,
} from 'react';
import { useThemeColors } from '../../providers/ThemeProvider';
import { cn } from '../../utils/cn';
import { Icon } from '../base/Icon';
import { LoadingSpinner } from '../display/LoadingSpinner';
import { Button } from '../forms/Button';

export interface SidebarAction {
	label: string;
	onClick: () => void | Promise<void>;
	variant?: 'primary' | 'secondary' | 'outline' | 'danger';
	icon?: IconDefinition;
	disabled?: boolean;
	loading?: boolean;
}

interface SidebarProps {
	isOpen: boolean;
	onClose: () => void;
	/**
	 * Called before close attempts (X button, backdrop click, ESC, click outside).
	 * Return false or Promise<false> to prevent closing.
	 * Useful for showing unsaved changes confirmation.
	 */
	onBeforeClose?: () => boolean | Promise<boolean>;
	title?: string;
	/** Rendered under the title in the header (ComponentHeader pattern) */
	subtitle?: React.ReactNode;
	/** While true, a centered spinner replaces the body content */
	loading?: boolean;
	children: React.ReactNode;
	width?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
	/**
	 * Replaces the panel's default background class. The default paints from
	 * the CRM theme (`colors.classes.bgPrimary`), which knows nothing about a
	 * tenant's brand tokens — so a consumer outside the CRM (the storefront)
	 * otherwise renders this in CRM chrome against a branded page. Pass the
	 * surface class that belongs to the host app; omit it and nothing changes.
	 *
	 * Applies to all three stacked surfaces — body, sticky header and sticky
	 * footer. They are separate elements that each paint themselves, so
	 * overriding only the body leaves a CRM-coloured header and footer framing
	 * a correctly-branded middle.
	 */
	panelClassName?: string;
	position?: 'left' | 'right';
	actions?: SidebarAction[];
	/**
	 * Optional note rendered in the sticky footer directly below the action
	 * buttons, on its own line. Use for a hint that qualifies the primary
	 * action (e.g. "a colleague with issue authority can then issue this").
	 */
	footerNote?: React.ReactNode;
	hideCloseButton?: boolean;
	modal?: boolean;
	/**
	 * When false, clicking the backdrop / outside the sidebar and pressing
	 * ESC won't close it — only the X button and explicit actions (Cancel,
	 * Save, etc.) do. Defaults to true to preserve existing behavior.
	 * Use for editors where a stray outside-click can lose unsaved edits.
	 */
	closeOnBackdrop?: boolean;
}

// Resolve `full` lazily — `window` isn't defined in node-env vitest runs,
// and module-scope access here was breaking any test that transitively
// imported this component.
const getDefaultWidth = (size: 'sm' | 'md' | 'lg' | 'xl' | 'full'): number => {
	switch (size) {
		case 'sm':
			return 448;
		case 'md':
			return 512;
		case 'lg':
			return 672;
		case 'xl':
			return 896;
		case 'full':
			return typeof window === 'undefined' ? 1280 : window.innerWidth;
	}
};

const minWidth = 350;

/**
 * Height (px) of the layout viewport currently occluded by the on-screen
 * keyboard. On iOS (Safari and Capacitor WKWebView) the soft keyboard does
 * NOT shrink the layout viewport, so a `fixed; bottom: 0` panel keeps
 * extending underneath it and its internal scroll area can never reach the
 * bottom fields. The visual viewport is the only reliable signal; lifting
 * the panel's `bottom` by this inset keeps the whole form reachable while
 * the keyboard is up. Returns 0 when the keyboard is closed, when the
 * platform resizes the layout viewport itself (Android browsers), or when
 * `visualViewport` is unavailable.
 */
/**
 * PD-15697 (reopened) — the native shell needs its own keyboard source.
 * The visual-viewport math below is correct in mobile Safari, but inside
 * the Capacitor WKWebView `visualViewport` does NOT shrink when the soft
 * keyboard opens (field-confirmed on the app build that shipped the first
 * fix: the panel stayed buried under the keyboard). The native shell's
 * Keyboard plugin listeners publish the true keyboard height as the
 * `--keyboard-height` CSS custom property (see NativeShellSetup), so the
 * hook reads BOTH sources and uses whichever reports occlusion.
 */
function readPublishedKeyboardHeight(): number {
	const raw = getComputedStyle(document.documentElement).getPropertyValue(
		'--keyboard-height',
	);
	const parsed = Number.parseFloat(raw);
	return Number.isFinite(parsed) && parsed > 1 ? Math.round(parsed) : 0;
}

export function useKeyboardBottomInset(enabled: boolean): number {
	const [inset, setInset] = useState(0);

	useEffect(() => {
		if (!enabled) {
			setInset(0);
			return undefined;
		}
		const vv = window.visualViewport;

		const update = (): void => {
			const occluded =
				vv != null ? window.innerHeight - vv.height - vv.offsetTop : 0;
			// Ignore sub-pixel noise from browser chrome show/hide; take the
			// larger of the browser-reported and native-published values.
			setInset(
				Math.max(
					occluded > 1 ? Math.round(occluded) : 0,
					readPublishedKeyboardHeight(),
				),
			);
		};

		update();
		vv?.addEventListener('resize', update);
		vv?.addEventListener('scroll', update);
		// Fired by the native shell whenever the Keyboard plugin reports a
		// height change — the only reliable signal inside the WKWebView.
		window.addEventListener('app-keyboard-height', update);

		return (): void => {
			vv?.removeEventListener('resize', update);
			vv?.removeEventListener('scroll', update);
			window.removeEventListener('app-keyboard-height', update);
		};
	}, [enabled]);

	return inset;
}

export function Sidebar({
	isOpen,
	onClose,
	onBeforeClose,
	title,
	subtitle,
	loading = false,
	children,
	width = 'lg',
	position = 'right',
	panelClassName,
	actions,
	footerNote,
	hideCloseButton = false,
	modal = true,
	closeOnBackdrop = true,
}: SidebarProps) {
	const colors = useThemeColors();
	const [sidebarWidth, setSidebarWidth] = useState(getDefaultWidth(width));
	const [isResizing, setIsResizing] = useState(false);
	const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
	const sidebarRef = useRef<HTMLDivElement>(null);
	const startXRef = useRef(0);
	const startWidthRef = useRef(0);
	const keyboardInset = useKeyboardBottomInset(isOpen);

	// Wrapper for close that respects onBeforeClose
	const handleClose = useCallback(async () => {
		if (onBeforeClose != null) {
			const shouldClose = await onBeforeClose();

			if (!shouldClose) {
				return;
			}
		}

		onClose();
	}, [onClose, onBeforeClose]);

	// Prevent body scroll when sidebar is open (only in modal mode)
	useEffect(() => {
		if (modal && isOpen) {
			document.body.style.overflow = 'hidden';
		} else if (modal) {
			document.body.style.overflow = '';
		}

		return () => {
			if (modal) {
				document.body.style.overflow = '';
			}
		};
	}, [isOpen, modal]);

	// Reset width when width prop changes or sidebar reopens
	useEffect(() => {
		if (isOpen) {
			setSidebarWidth(getDefaultWidth(width));
		}
	}, [width, isOpen]);

	// Update max width and mobile state on window resize
	useEffect(() => {
		const handleWindowResize = () => {
			const windowWidth = window.innerWidth;
			setIsMobile(windowWidth < 768);
			const newMaxWidth = windowWidth - 50;
			setSidebarWidth((current) => Math.min(current, newMaxWidth));
		};

		window.addEventListener('resize', handleWindowResize);
		return () => {
			window.removeEventListener('resize', handleWindowResize);
		};
	}, []);

	// Handle resize mouse events
	const handleMouseDown = useCallback(
		(e: React.MouseEvent) => {
			e.preventDefault();
			setIsResizing(true);
			startXRef.current = e.clientX;
			startWidthRef.current = sidebarWidth;
			document.body.style.cursor = 'col-resize';
			document.body.style.userSelect = 'none';
		},
		[sidebarWidth],
	);

	useEffect(() => {
		const handleMouseMove = (e: MouseEvent) => {
			if (!isResizing) {
				return;
			}

			const deltaX =
				position === 'right'
					? startXRef.current - e.clientX // For right sidebar, moving left increases width
					: e.clientX - startXRef.current; // For left sidebar, moving right increases width

			const currentMaxWidth = window.innerWidth - 50;
			const newWidth = Math.min(
				Math.max(startWidthRef.current + deltaX, minWidth),
				currentMaxWidth,
			);
			setSidebarWidth(newWidth);
		};

		const handleMouseUp = () => {
			if (isResizing) {
				setIsResizing(false);
				document.body.style.cursor = '';
				document.body.style.userSelect = '';
			}
		};

		if (isResizing) {
			document.addEventListener('mousemove', handleMouseMove);
			document.addEventListener('mouseup', handleMouseUp);
		}

		return () => {
			document.removeEventListener('mousemove', handleMouseMove);
			document.removeEventListener('mouseup', handleMouseUp);
		};
	}, [isResizing, position]);

	// Shared panel content used by both modal and non-modal modes
	const panelContent = (
		<div
			className={cn(
				'flex h-full flex-col shadow-xl',
				panelClassName ?? colors.classes.bgPrimary,
			)}
		>
			{/* Resize handle - only show on desktop */}
			{!isMobile && (
				<div
					className={cn(
						'absolute top-0 bottom-0 w-2 cursor-col-resize z-20 group',
						'bg-control-strong hover:bg-accent-badge transition-colors',
						position === 'right' ? 'left-0' : 'right-0',
						isResizing && 'bg-accent',
					)}
					onMouseDown={handleMouseDown}
					style={{
						touchAction: 'none',
						WebkitUserSelect: 'none',
						userSelect: 'none',
					}}
				>
					{/* Visual indicator - vertical dots pattern */}
					<div className="absolute inset-0 flex items-center justify-center">
						<div className="flex flex-col gap-1">
							<div className="w-1 h-1 rounded-full bg-neutral-solid group-hover:bg-surface" />
							<div className="w-1 h-1 rounded-full bg-neutral-solid group-hover:bg-surface" />
							<div className="w-1 h-1 rounded-full bg-neutral-solid group-hover:bg-surface" />
							<div className="w-1 h-1 rounded-full bg-neutral-solid group-hover:bg-surface" />
							<div className="w-1 h-1 rounded-full bg-neutral-solid group-hover:bg-surface" />
						</div>
					</div>

					{/* Hover tooltip */}
					<div
						className={cn(
							'absolute top-1/2 -translate-y-1/2 px-2 py-1 bg-success-solid-hover text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none',
							position === 'right' ? 'left-full ml-2' : 'right-full mr-2',
						)}
					>
						Drag to resize
					</div>
				</div>
			)}

			{/* Sticky Header */}
			<div
				className={cn(
					'flex-shrink-0 flex items-center justify-between px-6 py-4 border-b border-muted',
					panelClassName ?? colors.classes.bgPrimary,
					!isMobile && (position === 'right' ? 'pl-8' : 'pr-8'),
				)}
			>
				<div className="min-w-0">
					<h2
						className={cn('text-lg font-semibold', colors.classes.textPrimary)}
					>
						{title ?? 'Sidebar'}
					</h2>
					{subtitle != null && (
						<div
							className={cn('text-sm truncate', colors.classes.textSecondary)}
							title={typeof subtitle === 'string' ? subtitle : undefined}
						>
							{subtitle}
						</div>
					)}
				</div>
				{!hideCloseButton && (
					<button
						type="button"
						className={cn(
							'rounded-md p-2 hover:bg-opacity-10',
							colors.classes.textSecondary,
							colors.classes.bgHover,
						)}
						onClick={() => {
							void handleClose();
						}}
						aria-label="Close"
					>
						<Icon icon={faTimes} className="h-5 w-5" />
					</button>
				)}
			</div>

			{/* Scrollable Content */}
			<div
				className={cn(
					'flex-1 overflow-y-auto',
					position === 'right' ? 'pl-2' : 'pr-2',
				)}
			>
				{loading ? (
					<div className="flex h-full items-center justify-center py-8">
						<LoadingSpinner size="lg" />
					</div>
				) : (
					isOpen && children
				)}
			</div>

			{/* Sticky Footer with Actions */}
			{((actions != null && actions.length > 0) || footerNote != null) && (
				<div
					className={cn(
						'flex-shrink-0 px-6 py-4 border-t border-muted',
						panelClassName ?? colors.classes.bgPrimary,
					)}
				>
					{actions != null && actions.length > 0 && (
						<div className="flex justify-end gap-3">
							{actions.map((action, index) => (
								<Button
									key={index}
									variant={
										action.variant ??
										(index === actions.length - 1 ? 'primary' : 'outline')
									}
									onClick={action.onClick}
									disabled={action.disabled === true || action.loading === true}
									icon={action.icon}
								>
									{action.loading === true ? 'Loading...' : action.label}
								</Button>
							))}
						</div>
					)}
					{footerNote != null && <div className="mt-2">{footerNote}</div>}
				</div>
			)}
		</div>
	);

	// Click outside handler for non-modal mode
	useEffect(() => {
		if (modal || !isOpen || !closeOnBackdrop) {
			return;
		}

		const handleClickOutside = (event: MouseEvent) => {
			const target = event.target as Node;

			// Don't close if clicking inside this sidebar
			if (sidebarRef.current != null && sidebarRef.current.contains(target)) {
				return;
			}

			// Don't close if clicking inside a modal dialog (e.g., TaskDialog on top)
			// Dialog components from Headless UI have role="dialog"
			const targetElement =
				target instanceof Element ? target : target.parentElement;

			if (targetElement?.closest('[role="dialog"]') != null) {
				return;
			}

			void handleClose();
		};

		// Delay adding the listener to prevent immediate close from the click that opened it
		const timeoutId = setTimeout(() => {
			document.addEventListener('mousedown', handleClickOutside);
		}, 0);

		return () => {
			clearTimeout(timeoutId);
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [modal, isOpen, handleClose, closeOnBackdrop]);

	// Non-modal mode: render as a fixed panel without Dialog/backdrop
	if (!modal) {
		return (
			<Transition.Root show={isOpen} as={Fragment}>
				<div
					className="fixed bottom-0 z-40 pointer-events-none"
					style={{
						[position]: 0,
						top: 'calc(var(--env-banner-height, 0px) + env(safe-area-inset-top, 0px))',
						bottom: `${keyboardInset}px`,
					}}
				>
					<TransitionChild
						as={Fragment}
						enter="transform transition ease-in-out duration-300"
						enterFrom={
							position === 'right' ? 'translate-x-full' : '-translate-x-full'
						}
						enterTo="translate-x-0"
						leave="transform transition ease-in-out duration-300"
						leaveFrom="translate-x-0"
						leaveTo={
							position === 'right' ? 'translate-x-full' : '-translate-x-full'
						}
					>
						<div
							ref={sidebarRef}
							className="pointer-events-auto relative h-full"
							style={{ width: isMobile ? '100vw' : `${sidebarWidth}px` }}
						>
							{panelContent}
						</div>
					</TransitionChild>
				</div>
			</Transition.Root>
		);
	}

	// Modal mode: render with Dialog and backdrop (default behavior)
	return (
		<Transition.Root show={isOpen} as={Fragment}>
			<Dialog
				as="div"
				className="relative z-50"
				onClose={() => {
					// Headless UI fires Dialog onClose on backdrop click AND
					// ESC. When closeOnBackdrop is false we no-op so the user
					// has to click X or an explicit action — prevents losing
					// unsaved edits to a stray click/keypress. The X button
					// still calls handleClose directly so it remains the
					// always-available escape hatch.
					if (!closeOnBackdrop) {
						return;
					}
					void handleClose();
				}}
			>
				{/* Backdrop */}
				<TransitionChild
					as={Fragment}
					enter="ease-in-out duration-300"
					enterFrom="opacity-0"
					enterTo="opacity-100"
					leave="ease-in-out duration-300"
					leaveFrom="opacity-100"
					leaveTo="opacity-0"
				>
					<div
						className={cn(
							'fixed inset-x-0 bottom-0',
							colors.classes.backdropOverlay,
						)}
						style={{
							top: 'calc(var(--env-banner-height, 0px) + env(safe-area-inset-top, 0px))',
						}}
					/>
				</TransitionChild>

				<div
					className="fixed inset-x-0 bottom-0 overflow-hidden"
					style={{
						top: 'calc(var(--env-banner-height, 0px) + env(safe-area-inset-top, 0px))',
					}}
				>
					<div className="absolute inset-0 overflow-hidden">
						<div
							className={cn(
								'pointer-events-none fixed bottom-0 flex max-w-full',
								position === 'right'
									? cn('right-0', !isMobile && 'pl-10')
									: cn('left-0', !isMobile && 'pr-10'),
							)}
							style={{
								top: 'calc(var(--env-banner-height, 0px) + env(safe-area-inset-top, 0px))',
								bottom: `${keyboardInset}px`,
							}}
						>
							<TransitionChild
								as={Fragment}
								enter="transform transition ease-in-out duration-300"
								enterFrom={
									position === 'right'
										? 'translate-x-full'
										: '-translate-x-full'
								}
								enterTo="translate-x-0"
								leave="transform transition ease-in-out duration-300"
								leaveFrom="translate-x-0"
								leaveTo={
									position === 'right'
										? 'translate-x-full'
										: '-translate-x-full'
								}
							>
								<DialogPanel
									ref={sidebarRef}
									className="pointer-events-auto relative h-full"
									style={{ width: isMobile ? '100vw' : `${sidebarWidth}px` }}
								>
									{panelContent}
								</DialogPanel>
							</TransitionChild>
						</div>
					</div>
				</div>
			</Dialog>
		</Transition.Root>
	);
}
